import { FC, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Select, {
  components,
  MultiValue,
  OptionProps,
  MenuListProps,
  ValueContainerProps,
} from 'react-select';
import { multiSelectStyles } from '../../styles/reactSelectStyles';
import './MultiSelectDropdown.scss';

interface SelectOption {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  isLoading?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  menuIsOpen?: boolean;
  showSelectAll?: boolean;
  minWidth?: string;
  /**
   * Optional value -> display-label map. Omit and each option renders its own
   * value, as every existing consumer expects. Used by the TOC filter, whose
   * option values are canonical lowercase keys.
   */
  optionLabels?: Record<string, string>;
}

// Extra props passed through react-select's selectProps
interface ExtraSelectProps {
  filterText: string;
  setFilterText: (val: string) => void;
  allOptions: SelectOption[];
  selectedItems: string[];
  onSelectionChange: (selected: string[]) => void;
  showSelectAll: boolean;
  searchPlaceholder: string;
  selectAllLabel: string;
  selectedLabel: string;
  filterFn: (option: { label: string; value: string }) => boolean;
  optionLabels?: Record<string, string>;
}


const CheckboxOption: FC<OptionProps<SelectOption, true>> = ({
  isSelected,
  label: optionLabel,
  ...rest
}) => (
  <components.Option isSelected={isSelected} label={optionLabel} {...rest}>
    <span className="multi-select-dropdown__option">
      <input
        type="checkbox"
        checked={isSelected}
        onChange={() => {}}
        className="multi-select-dropdown__checkbox"
      />
      <span className="multi-select-dropdown__option-text">{optionLabel}</span>
    </span>
  </components.Option>
);

const CustomMenuList: FC<MenuListProps<SelectOption, true>> = ({ children, ...rest }) => {
  const extra = rest.selectProps as unknown as ExtraSelectProps;
  const {
    filterText,
    setFilterText,
    allOptions,
    selectedItems,
    onSelectionChange,
    showSelectAll,
    searchPlaceholder,
    selectAllLabel,
    filterFn,
  }: ExtraSelectProps = extra;

  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const filteredOptions = allOptions.filter((opt) =>
    filterFn({ label: opt.label, value: opt.value })
  );

  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => selectedItems.includes(opt.value));

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredValues = new Set(filteredOptions.map((opt) => opt.value));
      onSelectionChange(selectedItems.filter((s) => !filteredValues.has(s)));
    } else {
      const existing = new Set(selectedItems);
      const merged = [...selectedItems, ...filteredOptions.filter((opt) => !existing.has(opt.value)).map((opt) => opt.value)];
      onSelectionChange(merged);
    }
  };

  return (
    <components.MenuList {...rest}>
      <div className="multi-select-dropdown__search">
        <input
          ref={searchRef}
          type="text"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          placeholder={searchPlaceholder}
          className="multi-select-dropdown__search-input"
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        />
      </div>
      {showSelectAll && filteredOptions.length > 0 && (
        <div
          className="multi-select-dropdown__select-all"
          role="option"
          aria-selected={allFilteredSelected}
          tabIndex={0}
          onClick={handleSelectAll}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSelectAll(); }}
        >
          <span className="multi-select-dropdown__option">
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={() => {}}
              className="multi-select-dropdown__checkbox"
              tabIndex={-1}
            />
            <span className="multi-select-dropdown__option-text">
              {selectAllLabel} ({filteredOptions.length})
            </span>
          </span>
        </div>
      )}
      {children}
    </components.MenuList>
  );
};

const CustomValueContainer: FC<ValueContainerProps<SelectOption, true>> = ({
  children,
  ...rest
}) => {
  const { selectedItems, selectedLabel, optionLabels } = rest.selectProps as unknown as ExtraSelectProps;
  const count = selectedItems.length;
  const { placeholder } = rest.selectProps;

  return (
    <components.ValueContainer {...rest}>
      {count === 0
        ? placeholder
        : (
          <span style={{ color: 'var(--text)' }}>
            {count === 1 ? (optionLabels?.[selectedItems[0]] ?? selectedItems[0]) : `${count} ${selectedLabel}`}
          </span>
        )}
    </components.ValueContainer>
  );
};

const customComponents = {
  Option: CheckboxOption,
  MenuList: CustomMenuList,
  ValueContainer: CustomValueContainer,
};

const MultiSelectDropdown: FC<MultiSelectDropdownProps> = ({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Select...',
  isLoading = false,
  onOpen,
  onClose,
  menuIsOpen,
  showSelectAll = true,
  minWidth,
  optionLabels,
}) => {
  const { t } = useTranslation();
  const [filterText, setFilterText] = useState('');

  const selectOptions = useMemo<SelectOption[]>(
    () => options.map((o) => ({ value: o, label: optionLabels?.[o] ?? o })),
    [options, optionLabels]
  );

  const selectedValues = useMemo<SelectOption[]>(
    () => selected.map((s) => ({ value: s, label: optionLabels?.[s] ?? s })),
    [selected, optionLabels]
  );

  const handleChange = (newValue: MultiValue<SelectOption>) => {
    onChange(newValue.map((v) => v.value));
  };

  const filterOption = useCallback(
    (option: { label: string; value: string }) => {
      if (!filterText) return true;
      return option.label.toLowerCase().includes(filterText.toLowerCase());
    },
    [filterText]
  );

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = menuIsOpen !== undefined;
  const open = menuIsOpen ?? internalOpen;

  const handleMenuOpen = () => {
    if (!isControlled) setInternalOpen(true);
    onOpen?.();
  };

  const handleMenuClose = useCallback(() => {
    if (!isControlled) setInternalOpen(false);
    setFilterText('');
    onClose?.();
  }, [isControlled, onClose]);

  // Close on outside click. react-select's own blur-based close is unreliable
  // here: the custom in-menu search input steals focus, so react-select never
  // fires onMenuClose on an outside click (which left the controlled
  // advanced-filter dropdowns stuck open).
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        handleMenuClose();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open, handleMenuClose]);

  return (
    <div
      ref={wrapperRef}
      className="multi-select-dropdown"
      style={minWidth ? { minWidth } : undefined}
    >
      <span className="multi-select-dropdown__label">{label}</span>
      <Select<SelectOption, true>
        isMulti
        isSearchable={false}
        options={selectOptions}
        value={selectedValues}
        onChange={handleChange}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        isLoading={isLoading}
        isDisabled={isLoading}
        placeholder={placeholder}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        menuIsOpen={open}
        filterOption={filterOption}
        styles={multiSelectStyles}
        components={customComponents}
        noOptionsMessage={() => t('No options found')}
        {...{
          filterText,
          setFilterText,
          allOptions: selectOptions,
          selectedItems: selected,
          onSelectionChange: onChange,
          showSelectAll,
          searchPlaceholder: t('Search...'),
          selectAllLabel: t('Select All'),
          selectedLabel: t('selected'),
          filterFn: filterOption,
          optionLabels,
        } as any}
      />
    </div>
  );
};

export default MultiSelectDropdown;
